"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useRouter, usePathname } from "next/navigation";
import { motion, AnimatePresence, useMotionValue, useDragControls } from "framer-motion";
import {
  Sparkles,
  X,
  Minus,
  Send,
  Mic,
  MicOff,
  Square,
  Trash2,
  Loader2,
  ExternalLink,
  Copy,
  Check,
  GripVertical,
} from "lucide-react";
import { useAIChat, type ChatMessage } from "@/hooks/use-ai-chat";
import { useVoiceIntentDictation, type VoiceIntentResult } from "@/hooks/use-voice-intent-dictation";
import { VoiceProspectModal } from "@/components/crm/VoiceProspectModal";
import { fetchExtractedProspect, type ExtractedProspect } from "@/lib/extract-prospect";
import {
  executeVoiceAction,
  type VoiceAction,
} from "@/services/voiceActions";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function MessageBubble({
  message,
  onAction,
}: {
  message: ChatMessage;
  onAction: (action: any) => void;
}) {
  const [copied, setCopied] = useState(false);
  const isUser = message.role === "user";

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={cn("flex gap-2", isUser ? "justify-end" : "justify-start")}>
      {!isUser && (
        <div className="flex-shrink-0 mt-1 h-6 w-6 rounded-full bg-indigo-600 flex items-center justify-center">
          <Sparkles className="h-3 w-3 text-white" />
        </div>
      )}
      <div
        className={cn(
          "max-w-[85%] rounded-xl px-3 py-2 text-sm",
          isUser
            ? "bg-indigo-600 text-white"
            : "bg-muted/60 text-foreground"
        )}
      >
        <div className="whitespace-pre-wrap break-words">{message.content}</div>
        {message.isStreaming && (
          <span className="inline-block ml-1 animate-pulse">|</span>
        )}
        {!isUser && !message.isStreaming && message.content && (
          <div className="flex items-center gap-1 mt-1.5 -mb-0.5">
            <button
              onClick={handleCopy}
              className="text-muted-foreground/50 hover:text-muted-foreground transition-colors"
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            </button>
          </div>
        )}
        {message.action && message.action.type !== "none" && (
          <button
            onClick={() => onAction(message.action)}
            className="mt-2 flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            <ExternalLink className="h-3 w-3" />
            Exécuter l&apos;action
          </button>
        )}
      </div>
    </div>
  );
}

export function AIAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [input, setInput] = useState("");
  const [prospectModalOpen, setProspectModalOpen] = useState(false);
  const [prospectData, setProspectData] = useState<ExtractedProspect | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const pathname = usePathname();

  const { messages, isLoading, sendMessage, stopGeneration, clearMessages, pageContext } =
    useAIChat();

  const [voicePending, setVoicePending] = useState<{
    action: VoiceAction;
    transcript: string;
  } | null>(null);

  const handleVoiceParsed = useCallback((result: VoiceIntentResult) => {
    const { action, transcript } = result;
    if (action.type === "question") {
      setInput((prev) => (prev ? `${prev} ${transcript}`.trim() : transcript));
      toast.info("Texte ajouté au message — modifiez si besoin puis envoyez.");
      return;
    }
    setVoicePending({ action, transcript });
  }, []);

  const intentVoice = useVoiceIntentDictation(pathname, handleVoiceParsed);

  const handleMicClick = () => {
    if (intentVoice.isListening) intentVoice.stop();
    else intentVoice.start();
  };

  const handleConfirmVoice = async () => {
    if (!voicePending) return;
    const { action, transcript } = voicePending;
    setVoicePending(null);
    try {
      if (action.type === "create_prospect") {
        const data = await fetchExtractedProspect(transcript);
        setProspectData(data);
        setProspectModalOpen(true);
        toast.success("Vérifiez les champs puis créez le prospect.");
        return;
      }
      const msg = await executeVoiceAction(action, router);
      toast.success(msg);
    } catch (e) {
      console.error(e);
      toast.error("Impossible d'exécuter l'action vocale.");
    }
  };

  const micListening = intentVoice.isListening;
  const micInterim = intentVoice.transcript;

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  // Raccourci : ouvrir l'assistant (commande vocale dans le panneau)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && (e.key === "v" || e.key === "V")) {
        e.preventDefault();
        setIsOpen(true);
        setIsMinimized(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleSend = () => {
    if (!input.trim()) return;
    sendMessage(input);
    setInput("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAction = (action: any) => {
    if (!action) return;
    switch (action.type) {
      case "navigate":
        router.push(action.path);
        break;
      case "search_product":
      case "search_client":
        router.push(`/?q=${encodeURIComponent(action.query)}`);
        break;
    }
  };

  const handleSuggestion = (text: string) => {
    sendMessage(text);
  };

  const AI_POS_STORAGE_KEY = "doliui-ai-assistant-pos";
  const constraintsRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const dragControls = useDragControls();

  /** Garde le bloc entièrement dans la fenêtre (après drag ou chargement depuis localStorage). */
  const clampAssistantToViewport = useCallback(() => {
    const el = shellRef.current;
    if (!el || typeof window === "undefined") return;
    const rect = el.getBoundingClientRect();
    const m = 12;
    let dx = 0;
    let dy = 0;
    if (rect.top < m) dy += m - rect.top;
    if (rect.left < m) dx += m - rect.left;
    if (rect.bottom > window.innerHeight - m) dy -= rect.bottom - (window.innerHeight - m);
    if (rect.right > window.innerWidth - m) dx -= rect.right - (window.innerWidth - m);
    if (dx === 0 && dy === 0) return;
    x.set(x.get() + dx);
    y.set(y.get() + dy);
    try {
      localStorage.setItem(
        AI_POS_STORAGE_KEY,
        JSON.stringify({ x: x.get(), y: y.get() })
      );
    } catch {
      /* ignore */
    }
  }, [x, y]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem(AI_POS_STORAGE_KEY);
      if (!raw) return;
      const p = JSON.parse(raw) as { x?: number; y?: number };
      if (typeof p.x === "number") x.set(p.x);
      if (typeof p.y === "number") y.set(p.y);
    } catch {
      /* ignore */
    }
  }, [x, y]);

  /** Après chargement de la position sauvegardée, recadrer une fois le layout appliqué. */
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => clampAssistantToViewport());
    });
    return () => cancelAnimationFrame(id);
  }, [clampAssistantToViewport]);

  useEffect(() => {
    if (!isOpen) return;
    const t = requestAnimationFrame(() => clampAssistantToViewport());
    const onResize = () => clampAssistantToViewport();
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(t);
      window.removeEventListener("resize", onResize);
    };
  }, [isOpen, isMinimized, clampAssistantToViewport]);

  const persistAssistantPosition = useCallback(() => {
    try {
      localStorage.setItem(
        AI_POS_STORAGE_KEY,
        JSON.stringify({ x: x.get(), y: y.get() })
      );
    } catch {
      /* ignore */
    }
    clampAssistantToViewport();
  }, [x, y, clampAssistantToViewport]);

  /** Panneau ouvert et déplié : déplacement uniquement depuis l’en-tête (saisie et scroll préservés). */
  const dragFromHeaderOnly = isOpen && !isMinimized;

  /**
   * Au-dessus de la barre mobile (≈60px). z-[100] > nav (z-50).
   */
  const fabFixedPosition =
    "fixed z-[100] bottom-24 right-3 sm:right-4 md:bottom-6 md:right-6";

  return (
    <>
      {/* Zone de contrainte plein écran pour le drag (élément référencé par Framer Motion). */}
      <div
        ref={constraintsRef}
        className="pointer-events-none fixed inset-0 z-[90]"
        aria-hidden
      />

      <motion.div
        ref={shellRef}
        style={{ x, y }}
        drag
        dragConstraints={constraintsRef}
        dragMomentum={false}
        dragElastic={0.02}
        dragListener={!dragFromHeaderOnly}
        dragControls={dragControls}
        onDragEnd={persistAssistantPosition}
        className={cn(
          fabFixedPosition,
          "select-none max-w-[min(100%,calc(100vw-1.25rem))]"
        )}
      >
        <AnimatePresence mode="wait">
          {!isOpen ? (
            <motion.button
              key="fab"
              type="button"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={() => setIsOpen(true)}
              className={cn(
                "flex w-full cursor-grab items-center gap-2 rounded-full bg-indigo-600 px-4 py-2.5 text-white shadow-lg shadow-indigo-500/25 transition-all hover:bg-indigo-700 active:cursor-grabbing sm:px-5 sm:py-3"
              )}
              title="Glisser pour déplacer — clic pour ouvrir"
            >
              <GripVertical className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
              <Sparkles className="h-5 w-5 shrink-0" />
              <span className="text-sm font-medium">Assistant Sud Elec</span>
            </motion.button>
          ) : (
            <motion.div
              key="panel"
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
                height: isMinimized ? "auto" : undefined,
              }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className={cn(
                "flex min-h-0 w-[min(380px,calc(100vw-1.25rem))] max-w-[calc(100vw-1.25rem)] flex-col overflow-hidden rounded-2xl border bg-background shadow-2xl shadow-black/20",
                isMinimized
                  ? ""
                  : "h-[min(560px,calc(100svh-7rem))] max-h-[calc(100svh-7rem)]"
              )}
            >
            {/* Header — glisser depuis la zone titre (pas les boutons). */}
            <div className="flex shrink-0 items-center justify-between border-b bg-indigo-600 px-4 py-3 text-white">
              <div
                className={cn(
                  "flex min-w-0 flex-1 items-center gap-2",
                  dragFromHeaderOnly && "cursor-grab touch-none active:cursor-grabbing"
                )}
                onPointerDown={(e) => {
                  if (dragFromHeaderOnly) dragControls.start(e);
                }}
                title={dragFromHeaderOnly ? "Glisser pour déplacer la fenêtre" : undefined}
              >
                {dragFromHeaderOnly && (
                  <GripVertical className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                )}
                <Sparkles className="h-4 w-4 shrink-0" />
                <span className="font-medium text-sm">Assistant Sud Elec</span>
                <span className="text-[10px] opacity-70 bg-white/15 rounded px-1.5 py-0.5">
                  {pageContext.page}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-1" onPointerDown={(e) => e.stopPropagation()}>
                {messages.length > 0 && (
                  <button
                    onClick={clearMessages}
                    className="p-1 hover:bg-white/20 rounded transition-colors"
                    title="Effacer la conversation"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1 hover:bg-white/20 rounded transition-colors"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => { setIsOpen(false); setIsMinimized(false); }}
                  className="p-1 hover:bg-white/20 rounded transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {/* Body */}
            {!isMinimized && (
              <>
                <div
                  ref={scrollRef}
                  className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-3 space-y-3"
                >
                  {/* Welcome message */}
                  {messages.length === 0 && (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <div className="flex-shrink-0 mt-1 h-6 w-6 rounded-full bg-indigo-600 flex items-center justify-center">
                          <Sparkles className="h-3 w-3 text-white" />
                        </div>
                        <div className="bg-muted/60 rounded-xl px-3 py-2 text-sm">
                          <p>
                            Bonjour ! Je suis sur la page <strong>{pageContext.page}</strong>.
                            Comment puis-je vous aider ?
                          </p>
                        </div>
                      </div>

                      {/* Suggestions */}
                      <div className="pl-8 rounded-lg border border-indigo-500/20 bg-indigo-500/5 px-3 py-2 text-xs text-foreground/80">
                        <strong className="text-indigo-600 dark:text-indigo-400">Commande vocale :</strong>{" "}
                        dictée complète puis recliquez sur le micro pour arrêter. Création de{" "}
                        <strong>prospect</strong> (formulaire), <strong>devis</strong> (page nouveau avec
                        champs préremplis), navigation, actions CRM — ou question ajoutée au champ si l’IA
                        détecte une simple question.
                      </div>

                      {pageContext.suggestions && pageContext.suggestions.length > 0 && (
                        <div className="pl-8 space-y-1.5">
                          <p className="text-[11px] font-medium text-muted-foreground">
                            Suggestions :
                          </p>
                          {pageContext.suggestions.map((s, i) => (
                            <button
                              key={i}
                              onClick={() => handleSuggestion(s)}
                              className="block w-full text-left text-xs px-3 py-1.5 rounded-lg border border-border/50 hover:bg-muted/40 transition-colors text-foreground/80"
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Messages */}
                  {messages.map((msg) => (
                    <MessageBubble key={msg.id} message={msg} onAction={handleAction} />
                  ))}
                </div>

                {/* Dictée en cours */}
                {micListening && micInterim && (
                  <div className="shrink-0 border-t px-4 py-1 text-xs text-muted-foreground italic">
                    {micInterim}...
                  </div>
                )}

                {voicePending && (
                  <div className="shrink-0 space-y-2 border-t bg-muted/50 px-3 py-2.5">
                    <p className="text-xs font-medium text-foreground leading-snug">
                      {voicePending.action.description}
                    </p>
                    <p className="text-[10px] text-muted-foreground line-clamp-3 italic">
                      &laquo; {voicePending.transcript} &raquo;
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleConfirmVoice}
                        className="flex-1 rounded-lg bg-indigo-600 text-white text-xs font-medium py-2 hover:bg-indigo-700 transition-colors"
                      >
                        Exécuter
                      </button>
                      <button
                        type="button"
                        onClick={() => setVoicePending(null)}
                        className="rounded-lg border border-border bg-background text-xs font-medium px-3 py-2 hover:bg-muted/60 transition-colors"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}

                {/* Input area */}
                <div className="shrink-0 border-t p-3">
                  <div className="flex items-end gap-2">
                    <button
                      type="button"
                      onClick={handleMicClick}
                      disabled={intentVoice.isProcessing}
                      className={cn(
                        "flex-shrink-0 p-2 rounded-lg transition-colors",
                        micListening
                          ? "bg-red-500/20 text-red-500 animate-pulse"
                          : "hover:bg-muted text-muted-foreground",
                        intentVoice.isProcessing && "opacity-50 cursor-wait"
                      )}
                      title={
                        micListening
                          ? "Arrêter et analyser (prospect, devis, navigation…)"
                          : "Commande vocale — prospect, devis simple, navigation (Alt+V ouvre l’assistant)"
                      }
                    >
                      {intentVoice.isProcessing ? (
                        <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
                      ) : micListening ? (
                        <MicOff className="h-4 w-4" />
                      ) : (
                        <Mic className="h-4 w-4" />
                      )}
                    </button>

                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Tapez votre message..."
                      rows={1}
                      className="flex-1 resize-none rounded-lg border bg-muted/30 px-3 py-2 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-indigo-500 max-h-24"
                      style={{ minHeight: "36px" }}
                    />

                    {isLoading ? (
                      <button
                        onClick={stopGeneration}
                        className="flex-shrink-0 p-2 rounded-lg bg-orange-500/20 text-orange-500 hover:bg-orange-500/30 transition-colors"
                        title="Arrêter la génération"
                      >
                        <Square className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        onClick={handleSend}
                        disabled={!input.trim()}
                        className="flex-shrink-0 p-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Send className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {prospectData && (
        <VoiceProspectModal
          data={prospectData}
          open={prospectModalOpen}
          onClose={() => {
            setProspectModalOpen(false);
            setProspectData(null);
          }}
          onCreated={() => {
            setProspectModalOpen(false);
            setProspectData(null);
          }}
        />
      )}
    </>
  );
}
